import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { AdminStats } from "../types";

interface SubscriptionStatsProps {
  stats: AdminStats | undefined;
  onPlanClick?: (label: string, filterFn: (u: any) => boolean) => void;
}

export function SubscriptionStats({ stats, onPlanClick }: SubscriptionStatsProps) {
  const breakdown = (stats as any)?.planBreakdown as Record<string, { active: number; expired: number; cancelled: number; trial: number }> | undefined;

  const click = (label: string, planName: string, status?: string) => {
    if (!onPlanClick) return;
    onPlanClick(label, (u) => {
      const uPlan = u.planName || 'No Plan';
      if (uPlan !== planName) return false;
      if (status) return (u.subscriptionStatus || 'trial') === status;
      return true;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription by Plan</CardTitle>
        <CardDescription>Active and expired users per plan</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {breakdown && Object.entries(breakdown)
          .sort(([, a], [, b]) => (b.active + b.trial) - (a.active + a.trial))
          .map(([planName, counts]) => {
            const total = counts.active + counts.trial + counts.expired + counts.cancelled;
            return (
              <div key={planName} className="p-3 bg-primary/5 rounded-lg border space-y-2">
                <div
                  className={`flex items-center justify-between${onPlanClick ? " cursor-pointer hover:bg-primary/10 rounded p-1 -m-1" : ""}`}
                  onClick={() => click(`${planName} — All`, planName)}
                >
                  <span className="font-medium">{planName}</span>
                  <span className="text-xl font-bold text-primary">{total}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {counts.active > 0 && (
                    <Badge
                      className="cursor-pointer bg-green-100 text-green-800 hover:bg-green-200"
                      onClick={() => click(`${planName} — Active`, planName, 'active')}
                    >
                      Active: {counts.active}
                    </Badge>
                  )}
                  {counts.trial > 0 && (
                    <Badge
                      className="cursor-pointer bg-blue-100 text-blue-800 hover:bg-blue-200"
                      onClick={() => click(`${planName} — Trial`, planName, 'trial')}
                    >
                      Trial: {counts.trial}
                    </Badge>
                  )}
                  {counts.expired > 0 && (
                    <Badge
                      className="cursor-pointer bg-red-100 text-red-800 hover:bg-red-200"
                      onClick={() => click(`${planName} — Expired`, planName, 'expired')}
                    >
                      Expired: {counts.expired}
                    </Badge>
                  )}
                  {counts.cancelled > 0 && (
                    <Badge
                      className="cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200"
                      onClick={() => click(`${planName} — Cancelled`, planName, 'cancelled')}
                    >
                      Cancelled: {counts.cancelled}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}

        {!breakdown && stats?.subscriptionByPlan && (
          Object.entries(stats.subscriptionByPlan)
            .sort(([, a], [, b]) => b - a)
            .map(([planName, count]) => (
              <div
                key={planName}
                onClick={() => click(planName, planName)}
                className={`flex items-center justify-between p-3 bg-primary/5 rounded-lg border${onPlanClick ? " cursor-pointer hover:bg-primary/10" : ""}`}
              >
                <span className="font-medium">{planName}</span>
                <span className="text-2xl font-bold text-primary">{count}</span>
              </div>
            ))
        )}
      </CardContent>
    </Card>
  );
}
